            /**
 * Utility functions for organization/multi-tenancy support
 */

/**
 * Get organizationId from user context or localStorage
 * Only returns valid positive numbers - returns null for common users
 * IMPORTANT: Always reads from user object in localStorage, NOT from localStorage.organizationId
 * to avoid stale data issues
 * @param {Object} user - User object from AuthContext
 * @returns {number|null} - Organization ID or null if not found/invalid
 */
export const getOrganizationId = (user) => {
  let orgId = null;
  
  // First check user object (from AuthContext)
  if (user?.organizationId != null) {
    orgId = user.organizationId;
  }
  // Fallback to localStorage user object (source of truth)
  else {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser?.organizationId != null) {
          orgId = parsedUser.organizationId;
        }
      } catch (e) {
        console.error("Error parsing stored user:", e);
      }
    }
    
    // DO NOT fallback to localStorage.getItem("organizationId") as it may contain stale data
    // The organizationId should always come from the user object which is updated during login
  }
  
  // Validate: only return valid positive numbers
  // Return null for common users (no organizationId) so they use base tables
  if (orgId != null && orgId !== "" && !isNaN(orgId)) {
    const numOrgId = Number(orgId);
    return numOrgId > 0 ? numOrgId : null;
  }
  
  return null;
};

/**
 * Check if user belongs to a hospital organization (subscription-based)
 * @param {Object} user - User object from AuthContext
 * @returns {boolean} - True if user has organizationId
 */
export const hasOrganization = (user) => {
  return getOrganizationId(user) !== null;
};

/**
 * Filter data array to only include items that belong to the user's organization
 * This is a defensive measure in case the backend returns mixed data from base and organization tables
 * @param {Array} data - Array of data items (patients, staff, doctors, etc.)
 * @param {Object} user - User object from AuthContext (optional, will read from localStorage if not provided)
 * @returns {Array} - Filtered array containing only organization-specific data
 */
export const filterByOrganization = (data, user = null) => {
  // If data is not an array, return as-is
  if (!Array.isArray(data)) {
    return data;
  }
  
  const organizationId = getOrganizationId(user);
  
  // If user has no organizationId (common user), return all data (they use base tables)
  if (!organizationId) {
    return data;
  }
  
  // Check if any items have organizationId field
  const hasOrgIdField = data.some(item => {
    if (!item) return false;
    const itemOrgId = item.organizationId || item.organization_id;
    return itemOrgId != null;
  });
  
  // If no items have organizationId field, assume backend already filtered correctly
  // (items are from organization-specific table, so they belong to this organization)
  if (!hasOrgIdField) {
    console.log(`[filterByOrganization] No organizationId field found in data items. Assuming backend filtered correctly for organization ${organizationId}.`);
    return data;
  }
  
  // If some items have organizationId, filter to only include matching ones
  // Also exclude items with different organizationId (from other organizations)
  // Items without organizationId are excluded (they're from base tables)
  return data.filter(item => {
    if (!item) return false;
    
    const itemOrgId = item.organizationId || item.organization_id;
    
    // If item has no organizationId field, exclude it (safety measure)
    // This ensures we don't show base table data to organization users
    if (itemOrgId == null) {
      console.log(`[filterByOrganization] Excluding item without organizationId:`, item);
      return false;
    }
    
    // Compare organizationIds (handle both number and string)
    const matches = Number(itemOrgId) === Number(organizationId);
    if (!matches) {
      console.log(`[filterByOrganization] Excluding item with different organizationId (${itemOrgId} != ${organizationId}):`, item);
    }
    return matches;
  });
};

/**
 * Filter object/array data structure to only include organization-specific data
 * Works with arrays, objects with arrays, or nested structures
 * @param {any} data - Data structure to filter
 * @param {Object} user - User object from AuthContext (optional)
 * @returns {any} - Filtered data structure
 */
export const filterDataByOrganization = (data, user = null) => {
  const organizationId = getOrganizationId(user);
  
  // If user has no organizationId (common user), return data as-is
  if (!organizationId) {
    return data;
  }
  
  // If data is an array, filter it
  if (Array.isArray(data)) {
    return filterByOrganization(data, user);
  }
  
  // If data is an object, check if it contains arrays
  if (data && typeof data === 'object') {
    const filtered = {};
    for (const key in data) {
      if (Array.isArray(data[key])) {
        filtered[key] = filterByOrganization(data[key], user);
      } else {
        filtered[key] = data[key];
      }
    }
    return filtered;
  }
  
  // For other types, return as-is
  return data;
};

/**
 * Get organization name from user context or localStorage
 * @param {Object} user - User object from AuthContext
 * @returns {string|null} - Organization name or null if not found
 */
export const getOrganizationName = (user) => {
  // First check user object (from AuthContext)
  if (user?.organizationName != null) {
    return user.organizationName;
  }
  // Fallback to localStorage user object
  const storedUser = localStorage.getItem("user");
  if (storedUser) {
    try {
      const parsedUser = JSON.parse(storedUser);
      if (parsedUser?.organizationName != null) {
        return parsedUser.organizationName;
      }
    } catch (e) {
      console.error("Error parsing stored user:", e);
    }
  }
  return null;
};

/**
 * Fetch and cache organization name by organizationId
 * Tries hospital organizations first, then regular organizations
 * @param {number} organizationId - Organization ID
 * @returns {Promise<string|null>} - Organization name or null if not found
 */
export const fetchOrganizationName = async (organizationId) => {
  if (!organizationId || organizationId <= 0) {
    return null;
  }

  try {
    // Try hospital organizations first (subscription-based)
    const { getHospitalOrganizationById } = await import("../services/hospitalOrganizationService");
    try {
      const hospitalOrg = await getHospitalOrganizationById(organizationId);
      if (hospitalOrg?.name) {
        return hospitalOrg.name;
      }
    } catch (hospitalError) {
      // If hospital organization not found, try regular organizations
      console.log(`[fetchOrganizationName] Hospital organization not found, trying regular organizations...`);
    }

    // Try regular organizations (corporate/insurance/government)
    const { getOrganizationById } = await import("../services/organizationService");
    try {
      const org = await getOrganizationById(organizationId);
      if (org?.name) {
        return org.name;
      }
    } catch (orgError) {
      console.warn(`[fetchOrganizationName] Organization ${organizationId} not found in either service`);
    }
  } catch (error) {
    console.error(`[fetchOrganizationName] Error fetching organization name for ID ${organizationId}:`, error);
  }

  return null;
};

/**
 * Fetch and update organization name in user object
 * @param {number} organizationId - Organization ID
 * @returns {Promise<string|null>} - Organization name or null if not found
 */
export const fetchAndStoreOrganizationName = async (organizationId) => {
  const orgName = await fetchOrganizationName(organizationId);
  
  if (orgName) {
    // Update user object in localStorage
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        const updatedUser = {
          ...user,
          organizationName: orgName,
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        console.log(`[fetchAndStoreOrganizationName] ✅ Stored organization name: ${orgName}`);
        
        // Dispatch a custom event to notify AuthContext and other components
        // that the user object in localStorage has been updated
        window.dispatchEvent(new CustomEvent('userUpdated', { 
          detail: { updatedUser } 
        }));
      } catch (e) {
        console.error("[fetchAndStoreOrganizationName] Error updating user object:", e);
      }
    }
  }
  
  return orgName;
};

const extractAddressFromOrganization = (org) => {
  if (!org) return null;
  if (org.address) return org.address;
  if (org.location) return org.location;
  if (org.fullAddress) return org.fullAddress;

  const addressParts = [];
  if (org.streetAddress) addressParts.push(org.streetAddress);
  if (org.city) addressParts.push(org.city);
  if (org.state) addressParts.push(org.state);
  if (org.pincode) addressParts.push(org.pincode);
  if (org.zipCode) addressParts.push(org.zipCode);

  return addressParts.length > 0 ? addressParts.join(", ") : null;
};

const extractPhoneFromOrganization = (org) => {
  if (!org) return null;
  return org.phone || org.phoneNumber || org.mobile || org.contactNumber || null;
};

/**
 * Fetch organization address and phone by organizationId
 * @param {number} organizationId - Organization ID
 * @returns {Promise<{address: string|null, phone: string|null}>}
 */
export const fetchOrganizationProfile = async (organizationId) => {
  if (!organizationId || organizationId <= 0) {
    return { address: null, phone: null };
  }

  try {
    const { getHospitalOrganizationById } = await import("../services/hospitalOrganizationService");
    try {
      const hospitalOrg = await getHospitalOrganizationById(organizationId);
      if (hospitalOrg) {
        return {
          address: extractAddressFromOrganization(hospitalOrg),
          phone: extractPhoneFromOrganization(hospitalOrg),
        };
      }
    } catch (_) {
      // Fall through to regular organizations
    }

    const { getOrganizationById } = await import("../services/organizationService");
    try {
      const org = await getOrganizationById(organizationId);
      return {
        address: extractAddressFromOrganization(org),
        phone: extractPhoneFromOrganization(org),
      };
    } catch (_) {
      return { address: null, phone: null };
    }
  } catch (error) {
    console.error(`[fetchOrganizationProfile] Error fetching profile for ID ${organizationId}:`, error);
    return { address: null, phone: null };
  }
};

/**
 * Fetch organization address by organizationId
 * @param {number} organizationId - Organization ID
 * @returns {Promise<string|null>} - Organization address or null if not found
 */
export const fetchOrganizationAddress = async (organizationId) => {
  const profile = await fetchOrganizationProfile(organizationId);
  return profile.address;
};

/**
 * After editing hospital details, sync organization/admin names into the session
 * so Sidebar and AuthContext show updated values without re-login.
 */
export const syncSessionUserAfterHospitalEdit = ({
  organizationName,
  adminName,
} = {}) => {
  const storedUser = localStorage.getItem("user");
  if (!storedUser) {
    return null;
  }

  try {
    const user = JSON.parse(storedUser);
    const updatedUser = { ...user };

    if (organizationName != null && String(organizationName).trim() !== "") {
      updatedUser.organizationName = String(organizationName).trim();
    }

    if (adminName != null && String(adminName).trim() !== "") {
      const name = String(adminName).trim();
      updatedUser.username = name;
      updatedUser.userName = name;
      updatedUser.fullName = name;
    }

    localStorage.setItem("user", JSON.stringify(updatedUser));
    window.dispatchEvent(
      new CustomEvent("userUpdated", { detail: { updatedUser } })
    );
    return updatedUser;
  } catch (e) {
    console.error("[syncSessionUserAfterHospitalEdit] Error updating session user:", e);
    return null;
  }
};

/**
 * Convert organization name to URL-safe slug
 * @param {string} orgName - Organization name
 * @returns {string} - URL-safe slug
 */
export const getOrganizationSlug = (orgName) => {
  if (!orgName) return null;
  return orgName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Add organization name to URL as path prefix
 * @param {string} path - Current path
 * @param {Object} user - User object (optional)
 * @returns {string} - Path with organization prefix
 */
export const addOrgToUrl = (path, user = null) => {
  // Check if user is a base user (organizationId is null)
  const orgId = getOrganizationId(user);
  const isBaseUser = orgId === null;
  
  // Base users should NOT have organization prefix in URL
  if (isBaseUser) {
    console.log(`[addOrgToUrl] Base user detected - returning path without org prefix: ${path}`);
    return path;
  }
  
  const orgName = getOrganizationName(user);
  if (!orgName) {
    console.log(`[addOrgToUrl] No organization name found - returning path as-is: ${path}`);
    return path;
  }
  
  const orgSlug = getOrganizationSlug(orgName);
  if (!orgSlug) {
    console.log(`[addOrgToUrl] No organization slug - returning path as-is: ${path}`);
    return path;
  }
  
  // Remove leading slash if present, then add org prefix
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  
  // Don't add org prefix if it's already there or if it's a public route
  const publicRoutes = ['login', 'onboarding', 'patient'];
  if (publicRoutes.some(route => cleanPath.startsWith(route))) {
    return path;
  }
  
  // Check if path already starts with organization
  if (cleanPath.startsWith(`${orgSlug}/`)) {
    return path;
  }
  
  return `/${orgSlug}/${cleanPath}`;
};

/**
 * Get organization name from URL path
 * @param {string} pathname - Current pathname (optional, defaults to window.location.pathname)
 * @returns {string|null} - Organization slug from URL or null
 */
export const getOrgFromUrl = (pathname = null) => {
  const path = pathname || window.location.pathname;
  const parts = path.split('/').filter(Boolean);
  
  // First part after root could be organization name
  // But we need to check if it's not a known public route
  const publicRoutes = ['login', 'onboarding', 'patient'];
  if (parts.length > 0 && !publicRoutes.includes(parts[0])) {
    return parts[0];
  }
  
  return null;
};

/**
 * Remove organization prefix from URL path
 * @param {string} path - Current path
 * @param {string} orgSlug - Organization slug to remove
 * @returns {string} - Path without organization prefix
 */
export const removeOrgFromUrl = (path, orgSlug = null) => {
  if (!orgSlug) {
    orgSlug = getOrgFromUrl(path);
  }
  
  if (!orgSlug) return path;
  
  // Remove leading slash, split, filter out org slug, rejoin
  const parts = path.split('/').filter(Boolean);
  if (parts[0] === orgSlug) {
    parts.shift();
    return '/' + parts.join('/');
  }
  
  return path;
};

/**
 * Extract the actual route path from a URL that may contain organization prefix
 * @param {string} pathname - Pathname that may contain organization
 * @returns {string} - Clean path without organization prefix
 */
export const getRouteFromUrl = (pathname = null) => {
  const path = pathname || window.location.pathname;
  const orgSlug = getOrgFromUrl(path);
  
  if (orgSlug) {
    return removeOrgFromUrl(path, orgSlug);
  }
  
  return path;
};

